import React, { useState, useMemo } from "react";
import {
    Box, Typography, Stack, IconButton, Select, MenuItem,
    FormControl, InputLabel, Chip,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import {
    Chart as ChartJS, CategoryScale, LinearScale, BarElement,
    Tooltip as CJTooltip, Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(CategoryScale, LinearScale, BarElement, CJTooltip, Legend);

/* ── Design tokens (aligned with app primary palette) ── */
const SM = {
    sans:      "'Inter', system-ui, sans-serif",
    teal:      "#4338ca", tealSoft:  "#eef2ff", tealBd:   "#c7d2fe",  // indigo primary
    coral:     "#f97316", coralSoft: "#fff7ed",
    purple:    "#7c3aed", purpleSoft:"#f5f3ff",
    green:     "#16a34a", greenSoft: "#f0fdf4",
    red:       "#dc2626", redSoft:   "#fef2f2",
    amber:     "#d97706", amberSoft: "#fffbeb",
    page:      "#f8fafc", white:     "#ffffff",
    border:    "#e2e8f0", border2:   "#f1f5f9",
    txt1:      "#0f172a", txt2: "#334155", txt3: "#64748b", txt4: "#94a3b8",
};

/* ── Helpers ── */
const fmtDur = s => { if (!s) return "—"; const m = Math.floor(s / 60), sec = Math.floor(s % 60); return m ? `${m}m ${sec}s` : `${sec}s`; };
const pct    = (a, b) => b > 0 ? Math.round((a / b) * 100) : 0;
const fmt    = v => v == null ? "—" : typeof v === "number" && !Number.isInteger(v) ? v.toFixed(1) : String(v);

/* ── Metric definitions ── */
const METRICS = [
    { key: "totalCalls",      label: "Total Calls",        fmt: v => v ?? 0 },
    { key: "answered",        label: "Answered",           fmt: v => v ?? 0 },
    { key: "missed",          label: "Missed",             fmt: v => v ?? 0 },
    { key: "notAnswered",     label: "Not Answered",       fmt: v => v ?? 0 },
    { key: "answerRate",      label: "Answer Rate",        fmt: (v, sm) => `${pct(sm.answered, sm.totalCalls)}%` },
    { key: "avgDurationSec",  label: "Avg Duration",       fmt: v => fmtDur(v) },
    { key: "avgBillSec",      label: "Avg Bill Duration",  fmt: v => fmtDur(v) },
    { key: "avgLeadScore",    label: "Avg Lead Score",     fmt: v => v ? Math.round(v) : "—" },
    { key: "performanceScore",label: "Performance Score",  fmt: v => v ? Math.round(v) : "—" },
    { key: "rank",            label: "Rank",               fmt: v => v ? `#${v}` : "—" },
    { key: "connected",       label: "Connected Calls",    fmt: v => v ?? 0 },
];

/* ── Chart metrics (numeric only, for grouped bar) ── */
const CHART_METRICS = [
    { key: "totalCalls",       label: "Total Calls",      getValue: s => s.totalCalls || 0 },
    { key: "answered",         label: "Answered",         getValue: s => s.answered || 0 },
    { key: "answerRateNum",    label: "Answer Rate %",    getValue: s => pct(s.answered, s.totalCalls) },
    { key: "avgDurationSec",   label: "Avg Duration (s)", getValue: s => Math.round(s.avgDurationSec || 0) },
    { key: "avgLeadScore",     label: "Avg Lead Score",   getValue: s => Math.round(s.avgLeadScore || 0) },
    { key: "performanceScore", label: "Perf Score",       getValue: s => Math.round(s.performanceScore || 0) },
];

function getWinner(metricKey, smA, smB) {
    const higherBetter = ["totalCalls","answered","connected","answerRate","avgLeadScore","performanceScore","avgDurationSec","avgBillSec"];
    const lowerBetter  = ["missed","notAnswered","rank"];

    const aVal = metricKey === "answerRate" ? pct(smA.answered, smA.totalCalls) : (smA[metricKey] ?? 0);
    const bVal = metricKey === "answerRate" ? pct(smB.answered, smB.totalCalls) : (smB[metricKey] ?? 0);

    if (aVal === bVal) return "tie";
    if (higherBetter.includes(metricKey)) return aVal > bVal ? "A" : "B";
    if (lowerBetter.includes(metricKey))  return aVal < bVal ? "A" : "B";
    return "tie";
}

export default function SMComparisonPanel({ smA, allSmStats, overall, onClose }) {
    const [smBId, setSmBId] = useState("");

    const smB = useMemo(() => allSmStats?.find(s => (s.userId || s.agentName) === smBId) || null, [allSmStats, smBId]);

    const smAId = smA?.userId || smA?.agentName;
    const otherSms = (allSmStats || []).filter(s => (s.userId || s.agentName) !== smAId);

    /* Auto-select first option */
    const effectiveSmB = smB || (otherSms.length > 0 ? otherSms[0] : null);

    /* Team averages from overall */
    const teamAvg = useMemo(() => {
        if (!overall || !overall.totalSMs || overall.totalSMs === 0) return {};
        return {
            totalCalls:       Math.round((overall.totalCalls || 0) / overall.totalSMs),
            answered:         Math.round((overall.answered || 0) / overall.totalSMs),
            missed:           Math.round((overall.missed || 0) / overall.totalSMs),
            notAnswered:      Math.round((overall.notAnswered || 0) / overall.totalSMs),
            connected:        Math.round((overall.connected || 0) / overall.totalSMs),
            avgDurationSec:   overall.avgDurationSec || 0,
            avgBillSec:       overall.avgBillSec || 0,
            avgLeadScore:     overall.avgLeadScore || 0,
            performanceScore: overall.avgPerfScore || 0,
            answerRate:       overall.totalCalls > 0 ? pct(overall.answered, overall.totalCalls) : 0,
        };
    }, [overall]);

    /* Auto-insight text */
    const insightText = useMemo(() => {
        if (!smA || !effectiveSmB) return null;
        const aRate = pct(smA.answered, smA.totalCalls);
        const bRate = pct(effectiveSmB.answered, effectiveSmB.totalCalls);
        const diff  = Math.abs(aRate - bRate);
        const aName = smA.agentName || "SM A";
        const bName = effectiveSmB.agentName || "SM B";
        const aPerfScore = Math.round(smA.performanceScore || 0);
        const bPerfScore = Math.round(effectiveSmB.performanceScore || 0);
        const parts = [];
        if (diff > 0) parts.push(`${aRate > bRate ? aName : bName} answered ${diff}% more calls than ${aRate > bRate ? bName : aName} in this period.`);
        if (Math.abs(aPerfScore - bPerfScore) >= 3) parts.push(`${aPerfScore > bPerfScore ? aName : bName} has a higher performance score (${Math.max(aPerfScore, bPerfScore)} vs ${Math.min(aPerfScore, bPerfScore)}).`);
        return parts.length > 0 ? parts.join(" ") : `${aName} and ${bName} have similar performance in this period.`;
    }, [smA, effectiveSmB]);

    /* Grouped bar data */
    const barData = useMemo(() => {
        if (!smA || !effectiveSmB) return null;
        return {
            labels: CHART_METRICS.map(m => m.label),
            datasets: [
                {
                    label: smA.agentName || "SM A",
                    data: CHART_METRICS.map(m => m.getValue(smA)),
                    backgroundColor: alpha(SM.teal, 0.8),
                    borderRadius: 5,
                },
                {
                    label: effectiveSmB.agentName || "SM B",
                    data: CHART_METRICS.map(m => m.getValue(effectiveSmB)),
                    backgroundColor: alpha(SM.coral, 0.8),
                    borderRadius: 5,
                },
                {
                    label: "Team Avg",
                    data: CHART_METRICS.map(m => {
                        if (m.key === "answerRateNum") return teamAvg.answerRate || 0;
                        return Math.round(teamAvg[m.key] || 0);
                    }),
                    backgroundColor: alpha(SM.purple, 0.5),
                    borderRadius: 5,
                },
            ],
        };
    }, [smA, effectiveSmB, teamAvg]);

    const getCellBg = (metricKey, side) => {
        if (!smA || !effectiveSmB) return "transparent";
        const winner = getWinner(metricKey, smA, effectiveSmB);
        if (winner === "tie") return "transparent";
        return winner === side ? alpha(SM.teal, 0.08) : "transparent";
    };

    const getCellColor = (metricKey, side) => {
        if (!smA || !effectiveSmB) return SM.txt2;
        const winner = getWinner(metricKey, smA, effectiveSmB);
        return winner === side ? SM.teal : SM.txt2;
    };

    return (
        <Box sx={{
            position: "absolute", inset: 0, zIndex: 10,
            background: SM.page, display: "flex", flexDirection: "column",
            fontFamily: SM.sans, overflowY: "auto",
        }}>
            {/* Header */}
            <Box sx={{ background: SM.white, borderBottom: `1px solid ${SM.border}`, px: { xs: 2, sm: 3 }, py: 1.75, position: "sticky", top: 0, zIndex: 1 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                    <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
                        <Typography sx={{ fontSize: { xs: 13, sm: 15 }, fontWeight: 700, color: SM.teal, fontFamily: SM.sans }}>
                            {smA?.agentName || "SM A"}
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: SM.txt4, fontFamily: SM.sans }}>vs</Typography>
                        <FormControl size="small" sx={{ minWidth: 140 }}>
                            <Select
                                value={smBId || (otherSms[0] ? (otherSms[0].userId || otherSms[0].agentName) : "")}
                                onChange={e => setSmBId(e.target.value)}
                                displayEmpty
                                sx={{ fontSize: 13, fontFamily: SM.sans, borderRadius: 8, "& .MuiSelect-select": { py: 0.75, px: 1.5 } }}
                            >
                                {otherSms.map(s => (
                                    <MenuItem key={s.userId || s.agentName} value={s.userId || s.agentName} sx={{ fontSize: 13, fontFamily: SM.sans }}>
                                        {s.agentName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                    <IconButton size="small" onClick={onClose} sx={{ color: SM.txt3 }}>
                        <Close sx={{ fontSize: 20 }} />
                    </IconButton>
                </Stack>
            </Box>

            <Box sx={{ p: { xs: 2, sm: 2.5 }, display: "flex", flexDirection: "column", gap: 2.5 }}>
                {/* Comparison table */}
                {effectiveSmB && (
                    <Box sx={{ background: SM.white, border: `1px solid ${SM.border}`, borderRadius: 12, overflow: "hidden" }}>
                        {/* Table header */}
                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", background: SM.page, borderBottom: `1px solid ${SM.border}` }}>
                            {["Metric", smA?.agentName || "SM A", effectiveSmB.agentName || "SM B", "Team Avg"].map((h, i) => (
                                <Typography key={i} sx={{ fontSize: 11, fontWeight: 700, color: i === 1 ? SM.teal : i === 2 ? SM.coral : SM.txt3, fontFamily: SM.sans, py: 1.25, px: 1.5, textAlign: i > 0 ? "center" : "left" }}>
                                    {h}
                                </Typography>
                            ))}
                        </Box>
                        {/* Table rows */}
                        {METRICS.map((m, idx) => {
                            const aVal = m.key === "answerRate" ? pct(smA.answered, smA.totalCalls) : (smA?.[m.key] ?? 0);
                            const bVal = m.key === "answerRate" ? pct(effectiveSmB.answered, effectiveSmB.totalCalls) : (effectiveSmB?.[m.key] ?? 0);
                            const tVal = m.key === "answerRate" ? (teamAvg.answerRate || 0) : (teamAvg[m.key] ?? 0);
                            return (
                                <Box key={m.key} sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", borderBottom: idx < METRICS.length - 1 ? `1px solid ${SM.border2}` : "none", "&:hover": { background: alpha(SM.teal, 0.02) } }}>
                                    <Typography sx={{ fontSize: 12, color: SM.txt2, fontFamily: SM.sans, py: 1.1, px: 1.5, fontWeight: 500 }}>
                                        {m.label}
                                    </Typography>
                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", background: getCellBg(m.key, "A") }}>
                                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: getCellColor(m.key, "A"), fontFamily: SM.sans }}>
                                            {m.fmt(aVal, smA)}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", background: getCellBg(m.key, "B") }}>
                                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: getCellColor(m.key, "B"), fontFamily: SM.sans }}>
                                            {m.fmt(bVal, effectiveSmB)}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                        <Typography sx={{ fontSize: 12, color: SM.txt3, fontFamily: SM.sans }}>
                                            {m.fmt(tVal, teamAvg)}
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                )}

                {/* Grouped bar chart */}
                {barData && (
                    <Box sx={{ background: SM.white, border: `1px solid ${SM.border}`, borderRadius: 12, p: 2 }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: SM.txt2, fontFamily: SM.sans, mb: 1.5 }}>Key Metrics Comparison</Typography>
                        <Box sx={{ height: { xs: 220, sm: 270 } }}>
                            <Bar data={barData} options={{
                                responsive: true, maintainAspectRatio: false,
                                plugins: {
                                    legend: { display: true, position: "top", labels: { font: { family: SM.sans, size: 11 }, boxWidth: 12, padding: 12 } },
                                    tooltip: { titleFont: { family: SM.sans }, bodyFont: { family: SM.sans } },
                                },
                                scales: {
                                    x: { grid: { display: false }, ticks: { font: { family: SM.sans, size: 10 }, maxRotation: 30 } },
                                    y: { ticks: { font: { family: SM.sans, size: 10 } }, grid: { color: SM.border2 } },
                                },
                            }} />
                        </Box>
                    </Box>
                )}

                {/* Insight summary */}
                {insightText && (
                    <Box sx={{ background: alpha(SM.teal, 0.06), border: `1px solid ${SM.tealBd}`, borderRadius: 12, p: 2 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: SM.teal, fontFamily: SM.sans, mb: 0.5, textTransform: "uppercase", letterSpacing: 0.5 }}>
                            Insight
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: SM.txt1, fontFamily: SM.sans, lineHeight: 1.6 }}>
                            {insightText}
                        </Typography>
                    </Box>
                )}

                {!effectiveSmB && (
                    <Typography sx={{ fontSize: 13, color: SM.txt4, fontFamily: SM.sans, textAlign: "center", py: 4 }}>
                        Only one SM available — no comparison possible.
                    </Typography>
                )}
            </Box>
        </Box>
    );
}
